import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ClockIcon, PenToolIcon, HomeIcon, KeyIcon, UserXIcon, AlertCircleIcon } from 'lucide-react';
interface AwaitingResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScheduleSigning: () => void;
  onReject: (reason: 'wants_different' | 'rented_elsewhere' | 'no_collaboration', notes: string) => void;
  viewing: any;
  skipMainDecision?: boolean;
}
export function AwaitingResponseModal({
  isOpen,
  onClose,
  onScheduleSigning,
  onReject,
  viewing,
  skipMainDecision = false
}: AwaitingResponseModalProps) {
  const [selectedAction, setSelectedAction] = useState<'signing' | 'reject' | null>(null);
  const [rejectReason, setRejectReason] = useState<'wants_different' | 'rented_elsewhere' | 'no_collaboration' | null>(null);
  const [notes, setNotes] = useState('');
  // If skipMainDecision is true, go directly to reject reasons
  useEffect(() => {
    if (isOpen && skipMainDecision) {
      setSelectedAction('reject');
    } else if (isOpen && !skipMainDecision) {
      setSelectedAction(null);
      setRejectReason(null);
      setNotes('');
    }
  }, [isOpen, skipMainDecision]);
  const handleBack = () => {
    if (selectedAction === 'reject' && rejectReason) {
      setRejectReason(null);
      setNotes('');
    } else if (selectedAction && !skipMainDecision) {
      setSelectedAction(null);
      setRejectReason(null);
      setNotes('');
    } else {
      onClose();
    }
  };
  const handleSubmitReject = () => {
    if (!rejectReason) {
      alert('Te rog selectează un motiv');
      return;
    }
    if (!notes.trim()) {
      alert('Te rog adaugă detalii despre situație');
      return;
    }
    onReject(rejectReason, notes);
    // Reset
    setSelectedAction(null);
    setRejectReason(null);
    setNotes('');
  };
  return <AnimatePresence data-id="element-2647">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-2648" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2649">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" data-id="element-2650">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-5" data-id="element-2651">
                <div className="flex items-center justify-between" data-id="element-2652">
                  <div className="flex items-center space-x-3" data-id="element-2653">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-2654">
                      <ClockIcon className="w-6 h-6" data-id="element-2655" />
                    </div>
                    <div data-id="element-2656">
                      <h3 className="text-xl font-bold" data-id="element-2657">Răspuns Client</h3>
                      <p className="text-sm text-blue-100" data-id="element-2658">
                        {viewing?.clientName} - {viewing?.propertyId}
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-2659">
                    <XIcon className="w-6 h-6" data-id="element-2660" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-2661">
                <AnimatePresence mode="wait" data-id="element-2662">
                  {/* STEP 1: Main Decision */}
                  {!selectedAction && <motion.div key="main" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 20
              }} className="space-y-4" data-id="element-2663">
                      <div className="text-center mb-6" data-id="element-2664">
                        <h4 className="text-2xl font-bold text-[#132C53] mb-2" data-id="element-2665">
                          Clientul s-a decis?
                        </h4>
                        <p className="text-slate-600" data-id="element-2666">
                          Selectează răspunsul clientului
                        </p>
                      </div>

                      {/* Programare Semnare */}
                      <button onClick={() => {
                  onScheduleSigning();
                  onClose();
                }} className="group w-full relative overflow-hidden rounded-2xl border-2 border-emerald-200 hover:border-emerald-500 transition-all p-6 bg-gradient-to-br from-emerald-50 to-green-50 hover:shadow-xl text-left" data-id="element-2667">
                        <div className="flex items-center space-x-4" data-id="element-2668">
                          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-2669">
                            <PenToolIcon className="w-8 h-8 text-white" data-id="element-2670" />
                          </div>
                          <div className="flex-1" data-id="element-2671">
                            <p className="text-lg font-bold text-emerald-900 mb-1" data-id="element-2672">
                              Dorește să Semneze
                            </p>
                            <p className="text-sm text-emerald-700" data-id="element-2673">
                              Clientul este gata să închirieze proprietatea
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Reject Options */}
                      <button onClick={() => setSelectedAction('reject')} className="group w-full relative overflow-hidden rounded-2xl border-2 border-slate-200 hover:border-slate-400 transition-all p-6 bg-gradient-to-br from-slate-50 to-slate-100 hover:shadow-xl text-left" data-id="element-2674">
                        <div className="flex items-center space-x-4" data-id="element-2675">
                          <div className="w-16 h-16 bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-2676">
                            <UserXIcon className="w-8 h-8 text-white" data-id="element-2677" />
                          </div>
                          <div className="flex-1" data-id="element-2678">
                            <p className="text-lg font-bold text-slate-900 mb-1" data-id="element-2679">
                              Nu Dorește să Închirieze
                            </p>
                            <p className="text-sm text-slate-700" data-id="element-2680">
                              Clientul a refuzat sau nu mai colaborează
                            </p>
                          </div>
                        </div>
                      </button>
                    </motion.div>}

                  {/* STEP 2: Reject Reasons */}
                  {selectedAction === 'reject' && !rejectReason && <motion.div key="reject_reasons" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-2681">
                      <div className="text-center mb-6" data-id="element-2682">
                        <h4 className="text-2xl font-bold text-[#132C53] mb-2" data-id="element-2683">
                          Care este motivul?
                        </h4>
                        <p className="text-slate-600" data-id="element-2684">
                          Selectează situația clientului
                        </p>
                      </div>

                      {/* Vrea Alt Imobil */}
                      <button onClick={() => setRejectReason('wants_different')} className="group w-full relative overflow-hidden rounded-2xl border-2 border-blue-200 hover:border-blue-500 transition-all p-5 bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-xl text-left" data-id="element-2685">
                        <div className="flex items-center space-x-4" data-id="element-2686">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-2687">
                            <HomeIcon className="w-7 h-7 text-white" data-id="element-2688" />
                          </div>
                          <div className="flex-1" data-id="element-2689">
                            <p className="text-lg font-bold text-blue-900 mb-1" data-id="element-2690">
                              Vrea Alt Imobil
                            </p>
                            <p className="text-sm text-blue-700" data-id="element-2691">
                              Clientul caută alte proprietăți (rematching)
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* A Închiriat Altceva */}
                      <button onClick={() => setRejectReason('rented_elsewhere')} className="group w-full relative overflow-hidden rounded-2xl border-2 border-amber-200 hover:border-amber-500 transition-all p-5 bg-gradient-to-br from-amber-50 to-orange-50 hover:shadow-xl text-left" data-id="element-2692">
                        <div className="flex items-center space-x-4" data-id="element-2693">
                          <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-2694">
                            <KeyIcon className="w-7 h-7 text-white" data-id="element-2695" />
                          </div>
                          <div className="flex-1" data-id="element-2696">
                            <p className="text-lg font-bold text-amber-900 mb-1" data-id="element-2697">
                              A Închiriat Altceva
                            </p>
                            <p className="text-sm text-amber-700" data-id="element-2698">
                              Clientul a găsit altă proprietate
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Nu Mai Colaborează */}
                      <button onClick={() => setRejectReason('no_collaboration')} className="group w-full relative overflow-hidden rounded-2xl border-2 border-red-200 hover:border-red-500 transition-all p-5 bg-gradient-to-br from-red-50 to-pink-50 hover:shadow-xl text-left" data-id="element-2699">
                        <div className="flex items-center space-x-4" data-id="element-2700">
                          <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-2701">
                            <UserXIcon className="w-7 h-7 text-white" data-id="element-2702" />
                          </div>
                          <div className="flex-1" data-id="element-2703">
                            <p className="text-lg font-bold text-red-900 mb-1" data-id="element-2704">
                              Nu Mai Colaborează
                            </p>
                            <p className="text-sm text-red-700" data-id="element-2705">
                              Clientul nu mai răspunde / renunță
                            </p>
                          </div>
                        </div>
                      </button>
                    </motion.div>}

                  {/* STEP 3: Notes */}
                  {selectedAction === 'reject' && rejectReason && <motion.div key="notes" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-2706">
                      {/* Reason Summary */}
                      <div className={`rounded-xl p-4 border-l-4 ${rejectReason === 'wants_different' ? 'bg-blue-50 border-blue-500' : rejectReason === 'rented_elsewhere' ? 'bg-amber-50 border-amber-500' : 'bg-red-50 border-red-500'}`} data-id="element-2707">
                        <div className="flex items-center space-x-3" data-id="element-2708">
                          {rejectReason === 'wants_different' && <HomeIcon className="w-6 h-6 text-blue-600" data-id="element-2709" />}
                          {rejectReason === 'rented_elsewhere' && <KeyIcon className="w-6 h-6 text-amber-600" data-id="element-2710" />}
                          {rejectReason === 'no_collaboration' && <UserXIcon className="w-6 h-6 text-red-600" data-id="element-2711" />}
                          <div data-id="element-2712">
                            <p className={`font-bold ${rejectReason === 'wants_different' ? 'text-blue-900' : rejectReason === 'rented_elsewhere' ? 'text-amber-900' : 'text-red-900'}`} data-id="element-2713">
                              {rejectReason === 'wants_different' && 'Clientul vrea alt imobil'}
                              {rejectReason === 'rented_elsewhere' && 'Clientul a închiriat altceva'}
                              {rejectReason === 'no_collaboration' && 'Clientul nu mai colaborează'}
                            </p>
                            <p className={`text-sm ${rejectReason === 'wants_different' ? 'text-blue-700' : rejectReason === 'rented_elsewhere' ? 'text-amber-700' : 'text-red-700'}`} data-id="element-2714">
                              {rejectReason === 'wants_different' && 'Vizionarea va fi marcată ca finalizată - Rematching'}
                              {rejectReason === 'rented_elsewhere' && 'Vizionarea va fi marcată ca finalizată - Pierdut'}
                              {rejectReason === 'no_collaboration' && 'Vizionarea va fi marcată ca anulată'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Notes */}
                      <div data-id="element-2715">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2716">
                          Detalii despre situație *
                        </label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={5} placeholder={rejectReason === 'wants_different' ? 'ex: Clientul caută ceva mai spațios în aceeași zonă. Preferă 3 camere...' : rejectReason === 'rented_elsewhere' ? 'ex: Clientul a găsit o proprietate prin altă agenție. A semnat deja contractul...' : 'ex: Clientul nu mai răspunde la telefon de 3 zile. Am încercat să îl contactez de mai multe ori...'} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-2717" />
                      </div>

                      {/* Warning */}
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200" data-id="element-2718">
                        <div className="flex items-start space-x-3" data-id="element-2719">
                          <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-2720" />
                          <div data-id="element-2721">
                            <p className="text-sm font-bold text-amber-900 mb-1" data-id="element-2722">
                              Atenție
                            </p>
                            <p className="text-sm text-amber-700" data-id="element-2723">
                              Această acțiune va finaliza vizionarea și va
                              actualiza statusul clientului.
                              {rejectReason === 'wants_different' && ' Clientul va fi redirecționat către procesul de rematching.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200" data-id="element-2724">
                <button onClick={handleBack} className="px-5 py-2.5 rounded-xl font-semibold text-slate-700 hover:bg-slate-200 transition-colors" data-id="element-2725">
                  {selectedAction === 'reject' && rejectReason ? 'Înapoi' : skipMainDecision ? 'Anulează' : selectedAction ? 'Înapoi' : 'Anulează'}
                </button>
                {selectedAction === 'reject' && rejectReason && <button onClick={handleSubmitReject} className="px-6 py-2.5 rounded-xl font-bold bg-gradient-to-r from-slate-500 to-slate-600 text-white hover:shadow-lg hover:scale-105 transition-all" data-id="element-2726">
                    Confirmă Finalizare
                  </button>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}